import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container, Paper, Typography, Box, Button, Grid, Card, CardContent, CardActions,
  Chip, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Tabs, Tab, Divider
} from '@mui/material';
import { Add, Delete, Quiz, People, ContentCopy } from '@mui/icons-material';
import { toast } from 'react-toastify';
import DashboardSwitcher from '../components/DashboardSwitcher';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5006/api';

const TrainerDashboard = () => {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [viewTab, setViewTab] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [results, setResults] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', timeLimit: 1800, passingScore: 50, questionsPerCategory: 5, questionIds: [], scheduledAt: '' });

  const getToken = () => localStorage.getItem('adminToken');
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

  const fetchExams = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/exams`, authHeaders());
      setExams(res.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem('adminToken'); navigate('/auth'); }
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [navigate]);

  const fetchQuestions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/questions`, authHeaders());
      setQuestions(res.data);
    } catch (err) { /* ignore */ }
    // eslint-disable-next-line
  }, []);

  useEffect(() => { fetchExams(); fetchQuestions(); }, [fetchExams, fetchQuestions]);

  const handleOpenNew = () => {
    setEditingExam(null);
    setForm({ title: '', description: '', timeLimit: 1800, passingScore: 50, questionsPerCategory: 5, questionIds: [], scheduledAt: '' });
    setDialogOpen(true);
  };

  const handleOpenEdit = (exam) => {
    setEditingExam(exam);
    setForm({
      title: exam.title,
      description: exam.description || '',
      timeLimit: exam.timeLimit,
      passingScore: exam.passingScore,
      questionsPerCategory: exam.questionsPerCategory,
      questionIds: [],
      scheduledAt: exam.scheduledAt ? exam.scheduledAt.slice(0, 16) : ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    try {
      if (editingExam) {
        await axios.put(`${API_URL}/exams/${editingExam.id}`, form, authHeaders());
        toast.success('Exam updated');
      } else {
        await axios.post(`${API_URL}/exams`, form, authHeaders());
        toast.success('Exam created! You can now add questions and publish.');
      }
      setDialogOpen(false);
      fetchExams();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save'); }
  };

  const handlePublish = async (examId) => {
    try {
      const res = await axios.post(`${API_URL}/exams/${examId}/publish`, {}, authHeaders());
      toast.success('Exam published!');
      fetchExams();
      if (res.data.share_link) {
        navigator.clipboard.writeText(res.data.share_link);
        toast.info('Share link copied to clipboard!');
      }
    } catch (err) { toast.error('Failed to publish'); }
  };

  const handleDelete = async (examId) => {
    if (!window.confirm('Delete this exam?')) return;
    try {
      await axios.delete(`${API_URL}/exams/${examId}`, authHeaders());
      toast.success('Exam deleted');
      fetchExams();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const handleViewResults = async (exam) => {
    setSelectedExam(exam);
    setViewTab(1);
    try {
      const res = await axios.get(`${API_URL}/exams/${exam.id}/results`, authHeaders());
      setResults(res.data.results || []);
    } catch (err) { setResults([]); }
  };

  if (loading) return <><LinearProgress /></>;

  return (
    <>
      <DashboardSwitcher title="Trainer Panel" />
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4">Trainer Dashboard</Typography>
              <Typography variant="subtitle1" color="text.secondary">Create and manage your exams</Typography>
            </Box>
            <Box>
              <Button variant="contained" startIcon={<Add />} onClick={handleOpenNew}>New Exam</Button>
            </Box>
          </Box>

        <Tabs value={viewTab} onChange={(e, v) => setViewTab(v)} sx={{ mt: 2 }}>
          <Tab icon={<Quiz />} label="My Exams" iconPosition="start" />
          <Tab icon={<People />} label="Results" iconPosition="start" disabled={!selectedExam} />
        </Tabs>
      </Paper>

      {viewTab === 0 && (
        <>
          {exams.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom color="text.secondary">No exams yet</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={handleOpenNew}>Create Your First Exam</Button>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {exams.map(exam => (
                <Grid item xs={12} md={6} lg={4} key={exam.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6">{exam.title}</Typography>
                        <Chip size="small" label={exam.published ? 'Published' : 'Draft'} color={exam.published ? 'success' : 'default'} />
                      </Box>
                      {exam.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{exam.description}</Typography>}
                      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                        <Chip size="small" label={`${Math.floor(exam.timeLimit / 60)} min`} />
                        <Chip size="small" label={`${exam.questionCount || 0} questions`} />
                      </Box>
                      {exam.shareCode && <Typography variant="caption" display="block" sx={{ mt: 1, color: 'primary.main' }}>Code: {exam.shareCode}</Typography>}
                      {exam.scheduledAt && <Typography variant="caption" display="block" color="text.secondary">Scheduled: {new Date(exam.scheduledAt).toLocaleString()}</Typography>}
                    </CardContent>
                    <CardActions sx={{ p: 2, pt: 0, flexWrap: 'wrap', gap: 1 }}>
                      <Button size="small" onClick={() => handleViewResults(exam)}>Results</Button>
                      <Button size="small" onClick={() => handleOpenEdit(exam)}>Edit</Button>
                      {!exam.published && <Button size="small" color="success" onClick={() => handlePublish(exam.id)}>Publish</Button>}
                      {exam.published && exam.shareCode && (
                        <Tooltip title="Copy share link">
                          <IconButton size="small" onClick={() => {
                            const link = `${window.location.origin}/exam/${exam.shareCode}`;
                            navigator.clipboard.writeText(link);
                            toast.info('Link copied!');
                          }}><ContentCopy fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                      <IconButton size="small" color="error" onClick={() => handleDelete(exam.id)}><Delete fontSize="small" /></IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {viewTab === 1 && selectedExam && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h5">{selectedExam.title} — Results</Typography>
              <Typography variant="body2" color="text.secondary">{results.length} participant(s)</Typography>
            </Box>
            <Button onClick={() => setViewTab(0)}>Back to Exams</Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {results.length === 0 ? (
            <Typography color="text.secondary">No results yet.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Participant</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Score</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Report</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map(t => (
                    <TableRow key={t.id} hover>
                      <TableCell>{t.participantName || 'N/A'}</TableCell>
                      <TableCell>{t.participantEmail || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip size="small" color={t.score >= 50 ? 'success' : 'error'} label={t.score ? `${t.score.toFixed(1)}%` : 'N/A'} />
                      </TableCell>
                      <TableCell>{t.duration ? `${Math.floor(t.duration / 60)}m ${t.duration % 60}s` : 'N/A'}</TableCell>
                      <TableCell><Chip size="small" label={t.status} /></TableCell>
                      <TableCell>{t.startTime ? new Date(t.startTime).toLocaleDateString() : 'N/A'}</TableCell>
                      <TableCell>
                        <Button size="small" href={`${API_URL}/test/export/pdf/${t.id}`} target="_blank" rel="noopener">Download PDF</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Exam Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingExam ? 'Edit Exam' : 'Create New Exam'}</DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth label="Exam Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} margin="normal" required placeholder="e.g., Midterm Mathematics Exam" helperText={`${form.title.length}/100 characters`} inputProps={{ maxLength: 100 }} />
          <TextField fullWidth label="Description" multiline rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} margin="normal" placeholder="Brief description of the exam content and objectives" helperText={`${form.description.length}/500 characters`} inputProps={{ maxLength: 500 }} />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={4}>
              <TextField fullWidth label="Time Limit (sec)" type="number" value={form.timeLimit} onChange={e => setForm({...form, timeLimit: parseInt(e.target.value) || 1800})} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Passing Score %" type="number" value={form.passingScore} onChange={e => setForm({...form, passingScore: parseFloat(e.target.value) || 50})} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Questions/Category" type="number" value={form.questionsPerCategory} onChange={e => setForm({...form, questionsPerCategory: parseInt(e.target.value) || 5})} />
            </Grid>
          </Grid>
          <TextField
            fullWidth
            label="Schedule Start Date/Time (optional)"
            type="datetime-local"
            value={form.scheduledAt}
            onChange={e => setForm({...form, scheduledAt: e.target.value})}
            sx={{ mt: 2 }}
            InputLabelProps={{ shrink: true }}
            helperText="Leave empty to make exam available immediately after publishing"
          />

          {!editingExam && questions.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Select Questions (optional)</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Leave empty to auto-select from question bank. Check questions to include.
              </Typography>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ fontWeight: 600 }}>Select</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Question</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Difficulty</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {questions.map(q => (
                      <TableRow key={q.id} hover selected={form.questionIds.includes(q.id)}>
                        <TableCell padding="checkbox">
                          <input type="checkbox" checked={form.questionIds.includes(q.id)}
                            onChange={() => setForm({
                              ...form,
                              questionIds: form.questionIds.includes(q.id)
                                ? form.questionIds.filter(id => id !== q.id)
                                : [...form.questionIds, q.id]
                            })} />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.questionText}</TableCell>
                        <TableCell><Chip size="small" label={q.category} /></TableCell>
                        <TableCell><Chip size="small" color={q.difficulty === 'easy' ? 'success' : q.difficulty === 'hard' ? 'error' : 'warning'} label={q.difficulty} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>{editingExam ? 'Update' : 'Create Exam'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
    </>
  );
};

export default TrainerDashboard;
